import React, { useRef, useState } from "react"
import { Box, Button, Typography, CircularProgress, Alert } from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import DeleteIcon from "@mui/icons-material/Delete"
import ImageIcon from "@mui/icons-material/Image"
import {
    uploadOutletLogo,
    removeOutletLogo,
} from "../../services/outletSettingsService"
import { useTranslation } from "react-i18next"

interface LogoUploadProps {
    companyId: string
    outletId: string
    logoUrl: string | null
    onLogoChange: (logoUrl: string | null, logoMimeType: string | null) => void
}

const MAX_SIZE = 2 * 1024 * 1024 // 2 MB

const LogoUpload: React.FC<LogoUploadProps> = ({
    companyId,
    outletId,
    logoUrl,
    onLogoChange,
}) => {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState("")

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        // Client-side validation
        if (file.size > MAX_SIZE) {
            setError(String(t("settings.logoUpload.error.maxSize")))
            return
        }

        const allowed = ["image/png", "image/jpeg", "image/svg+xml"]
        if (!allowed.includes(file.type)) {
            setError(String(t("settings.logoUpload.error.invalidFormat")))
            return
        }

        setError("")
        setUploading(true)
        try {
            const result = await uploadOutletLogo(companyId, outletId, file)
            onLogoChange(result.logo_url, result.logo_mime_type)
        } catch (err: any) {
            setError(
                err.message || String(t("settings.logoUpload.error.upload"))
            )
        } finally {
            setUploading(false)
            // Reset file input
            if (fileInputRef.current) fileInputRef.current.value = ""
        }
    }

    const handleRemove = async () => {
        setUploading(true)
        setError("")
        try {
            await removeOutletLogo(companyId, outletId)
            onLogoChange(null, null)
        } catch (err: any) {
            setError(
                err.message || String(t("settings.logoUpload.error.remove"))
            )
        } finally {
            setUploading(false)
        }
    }

    const logoSrc = logoUrl ? `/api/uploads/${logoUrl}?t=${Date.now()}` : null

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {t("settings.logoUpload.title")}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    border: "1px dashed #d1d5db",
                    borderRadius: 2,
                    bgcolor: "#fafafa",
                }}
            >
                {/* Preview */}
                <Box
                    sx={{
                        width: 120,
                        height: 60,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    {logoSrc ? (
                        <img
                            src={logoSrc}
                            alt={String(t("settings.logoUpload.title"))}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                            }}
                        />
                    ) : (
                        <ImageIcon sx={{ color: "#d1d5db", fontSize: 32 }} />
                    )}
                </Box>

                {/* Actions */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml"
                        style={{ display: "none" }}
                        onChange={handleFileSelect}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                            uploading ? (
                                <CircularProgress size={14} />
                            ) : (
                                <CloudUploadIcon />
                            )
                        }
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ textTransform: "none" }}
                    >
                        {logoUrl
                            ? t("settings.logoUpload.replace")
                            : t("settings.logoUpload.upload")}
                    </Button>
                    {logoUrl && (
                        <Button
                            variant="text"
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            disabled={uploading}
                            onClick={handleRemove}
                            sx={{ textTransform: "none" }}
                        >
                            {t("settings.logoUpload.remove")}
                        </Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {t("settings.logoUpload.hint")}
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}
        </Box>
    )
}

export default LogoUpload
